import React, { useState } from "react";
import { Button, Box } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import Papa from "papaparse";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";

export default function UploadStaff() {
  const [previewData, setPreviewData] = useState([]);
  const [csvFile, setCsvFile] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const user = JSON.parse(sessionStorage.getItem("user"));
  const clientId = user?.client_id;
  const collegeId = user?.college_id;

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // DataGrid columns
  const columns = [
    { field: "sno", headerName: "S.No", width: 70 },
    { field: "staff_code", headerName: "Staff Code", width: 150 },
    { field: "salutation", headerName: "Salutation", width: 100 },
    { field: "staff_name", headerName: "Staff Name", width: 180 },
    { field: "aadhar_no", headerName: "Aadhar No", width: 130 },
    { field: "dob", headerName: "DOB", width: 110 },
    { field: "doj", headerName: "DOJ", width: 110 },
    { field: "dor", headerName: "DOR", width: 110 },
    { field: "gender", headerName: "Gender", width: 80 },
    { field: "qualification", headerName: "Qualification", width: 150 },
    { field: "designation", headerName: "Designation", width: 150 },
    { field: "staff_type", headerName: "Staff Type", width: 100 },
    { field: "doj_time", headerName: "DOJ Time", width: 100 },
    { field: "dor_time", headerName: "DOR Time", width: 100 },
    { field: "mobile", headerName: "Mobile", width: 130 },
    { field: "email", headerName: "Email", width: 180 },
    { field: "staff_status", headerName: "Status", width: 100 },
    { field: "workflow_status", headerName: "Workflow Status", width: 150 },
    {
      field: "errors",
      headerName: "Errors",
      width: 250,
      renderCell: (params) => (
        <span style={{ color: "red" }}>
          {params.value && params.value.length > 0 ? params.value.join(", ") : ""}
        </span>
      ),
    },
  ];

  // Handle CSV file selection
  const handleFileChange = (e) => setCsvFile(e.target.files[0]);

  // Preview CSV data with validation
  const handlePreview = () => {
    if (!csvFile) {
      alert("Please choose a CSV file first!");
      return;
    }

    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      complete: function (results) {
        const validatedData = results.data.map((row, index) => {
          let errors = [];

          if (!row.staff_code) errors.push("Missing staff_code");
          if (!row.staff_name) errors.push("Missing staff_name");
          if (row.mobile && !/^\d{10}$/.test(row.mobile)) errors.push("Invalid mobile");
          if (row.email && !/^\S+@\S+\.\S+$/.test(row.email)) errors.push("Invalid email");

          return { id: index + 1, sno: index + 1, ...row, errors };
        });
        setPreviewData(validatedData);
      },
    });
  };

  // Confirm & upload valid rows
  const handleConfirm = async () => {
    try {
      const validRows = previewData.filter((row) => !row.errors || row.errors.length === 0);
      if (validRows.length === 0) {
        alert("No valid rows to upload!");
        return;
      }

      const res = await axios.post(
        `${API_BASE_URL}/api/staff-upload/${clientId}/${collegeId}/data`,
        { staff: validRows }
      );

      alert(res.data.message || "Staff records uploaded successfully!");
      setPreviewData([]);
      setCsvFile(null);
    } catch (err) {
      console.error(err);
      alert("Error uploading data: " + (err.response?.data?.message || err.message));
    }
  };

  // Download CSV Template based on core_mstr_staff
  const handleDownloadTemplate = () => {
    const headers = [
      "staff_code",
      "salutation",
      "staff_name",
      "aadhar_no",
      "dob",
      "doj",
      "dor",
      "gender",
      "qualification",
      "designation",
      "staff_type",
      "doj_time",
      "dor_time",
      "mobile",
      "email",
      "staff_status",
      "workflow_status",
    ];

    const csv = Papa.unparse([headers]); // Only headers
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "staff_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* CSV Template Download */}
      <Button
        variant="contained"
        startIcon={<DownloadIcon />}
        sx={{
          backgroundColor: "#2196f3",
          color: "#fff",
          "&:hover": { backgroundColor: "#1976d2" },
          mr: 2,
        }}
        onClick={handleDownloadTemplate}
      >
        Download CSV
      </Button>

      {/* File Upload */}
      <input
        type="file"
        accept=".csv"
        style={{ display: "none" }}
        id="upload-csv"
        onChange={handleFileChange}
      />
      <label htmlFor="upload-csv">
        <Button
          variant="contained"
          component="span"
          startIcon={<CloudUploadIcon />}
          sx={{
            backgroundColor: "#1976d2",
            color: "#fff",
            "&:hover": { backgroundColor: "#115293" },
          }}
        >
          Choose Staff CSV
        </Button>
      </label>

      <Button
        sx={{
          ml: 2,
          backgroundColor: "#ff9800",
          color: "#fff",
          "&:hover": { backgroundColor: "#c77700" },
        }}
        variant="contained"
        onClick={handlePreview}
      >
        Preview Staff CSV
      </Button>

      {/* Data Preview & Upload */}
      {previewData.length > 0 && (
        <>
          <Box sx={{ mt: 3, height: 500 }}>
            <DataGrid
              rows={previewData}
              columns={columns}
              pageSize={pageSize} // controlled pageSize
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)} // update state
              rowsPerPageOptions={[5, 10, 25, 50]}
              disableRowSelectionOnClick
              pagination
            />
          </Box>

          <Button
            sx={{
              mt: 2,
              backgroundColor: "#4caf50",
              color: "#fff",
              "&:hover": { backgroundColor: "#357a38" },
              mr: 2,
            }}
            variant="contained"
            onClick={handleConfirm}
          >
            Confirm & Upload
          </Button>
        </>
      )}
    </Box>
  );
}
